// Apparence : une palette (chaude, par défaut, bleue ou verte) et un mode (celui
// du système, clair ou sombre). Les palettes sont déclarées séparément dans
// styles.css, jamais obtenues par inversion (ADR-0003 § 4 du POC).
//
// Le choix se règle dans Paramètres et se garde dans le navigateur
// (localStorage) : on le retrouve au rechargement et à la visite suivante. Le
// stockage peut être absent ou refusé (navigation privée) : on retombe alors
// sur la palette par défaut au mode du système, sans erreur.

import { createSignal } from "solid-js";

export type Palette = "chaud" | "bleu" | "vert";
export type Mode = "auto" | "clair" | "sombre";
export interface Apparence {
  palette: Palette;
  mode: Mode;
}

export const PALETTES: { id: Palette; libelle: string }[] = [
  { id: "chaud", libelle: "Chaud et vert" },
  { id: "bleu", libelle: "Froid et bleu" },
  { id: "vert", libelle: "Tout vert" },
];
export const MODES: { id: Mode; libelle: string }[] = [
  { id: "auto", libelle: "Système" },
  { id: "clair", libelle: "Clair" },
  { id: "sombre", libelle: "Sombre" },
];

export const APPARENCE_PAR_DEFAUT: Apparence = { palette: "chaud", mode: "auto" };
export const CLE_STOCKAGE = "parsada-map:apparence";

const estPalette = (v: unknown): v is Palette => v === "chaud" || v === "bleu" || v === "vert";
const estMode = (v: unknown): v is Mode => v === "auto" || v === "clair" || v === "sombre";

/** Préférences enregistrées, validées : une valeur inconnue revient au défaut. */
export function lireApparence(): Apparence {
  try {
    const brut = JSON.parse(window.localStorage.getItem(CLE_STOCKAGE) ?? "{}") as Record<
      string,
      unknown
    >;
    return {
      // « neutre » était l'ancienne palette par défaut, rarement choisie : elle
      // devient la nouvelle, et la bleue reste à un clic.
      palette: estPalette(brut.palette) ? brut.palette : APPARENCE_PAR_DEFAUT.palette,
      mode: estMode(brut.mode) ? brut.mode : APPARENCE_PAR_DEFAUT.mode,
    };
  } catch {
    return { ...APPARENCE_PAR_DEFAUT };
  }
}

function enregistrer(a: Apparence): void {
  try {
    window.localStorage.setItem(CLE_STOCKAGE, JSON.stringify(a));
  } catch {
    // Stockage indisponible : le choix vaut pour la page ouverte seulement.
  }
}

/**
 * Pose l'apparence sur la racine. Sans attribut, la feuille de style applique la
 * palette par défaut au mode du système : c'est aussi ce qu'on voit avant le script.
 */
export function appliquerApparence(a: Apparence): void {
  const racine = document.documentElement;
  if (a.palette === "chaud") racine.removeAttribute("data-palette");
  else racine.setAttribute("data-palette", a.palette);
  if (a.mode === "auto") racine.removeAttribute("data-theme");
  else racine.setAttribute("data-theme", a.mode === "sombre" ? "dark" : "light");
}

/** Applique les préférences enregistrées, avant le premier rendu. */
export function appliquerApparenceInitiale(): void {
  appliquerApparence(lireApparence());
}

/** L'apparence courante et de quoi la changer ; chaque changement est enregistré. */
export function creerApparence() {
  const [apparence, setApparence] = createSignal<Apparence>(lireApparence());
  const changer = (modif: Partial<Apparence>) => {
    const suivante = { ...apparence(), ...modif };
    appliquerApparence(suivante);
    enregistrer(suivante);
    setApparence(suivante);
  };
  return { apparence, changer };
}
